/**
 * Catalog API client — wrapper HTTP pro frontend.
 *
 * OpenSpec add-categorized-catalog-guideline (PR 2 Fase 3).
 *
 * Métodos:
 *   - listCatalogItems(filters?) → CatalogItem[]
 *   - getCatalogItem(productId) → CatalogItem
 *   - createCatalogItem(payload) → CatalogItem
 *   - updateCatalogItem(productId, patch) → CatalogItem
 *   - deleteCatalogItem(productId) → void
 *   - askCatalog(query, categoryId?, topK?) → {answer, sources}
 */
import type {
  CatalogItem,
  CreateCatalogItemRequest,
  UpdateCatalogItemRequest,
  CatalogItemType,
  CatalogItemStatus,
  CatalogAskResponse,
} from '../types';

// Padroniza com services/api.ts: REACT_APP_API_URL já inclui o sufixo /api
// (default local `http://localhost:3010/api`; em produção vem do deploy.sh).
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3010/api';

function authHeader(): Record<string, string> {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let payload: { error?: string; message?: string; field?: string; reason?: string } | null = null;
    try {
      payload = await res.json();
    } catch {
      // ignore
    }
    const detail = payload?.reason
      ? `${payload.field}: ${payload.reason}`
      : payload?.message || `HTTP ${res.status}`;
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export interface ListCatalogFilters {
  categoryId?: string;
  type?: CatalogItemType;
  status?: CatalogItemStatus;
}

export async function listCatalogItems(filters: ListCatalogFilters = {}): Promise<CatalogItem[]> {
  const qs = new URLSearchParams();
  if (filters.categoryId) qs.set('categoryId', filters.categoryId);
  if (filters.type) qs.set('type', filters.type);
  if (filters.status) qs.set('status', filters.status);
  const query = qs.toString();
  const url = `${API_BASE}/catalog/items${query ? `?${query}` : ''}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  const json = await handleResponse<{ items: CatalogItem[] }>(res);
  return json.items;
}

export async function getCatalogItem(productId: string): Promise<CatalogItem> {
  const res = await fetch(`${API_BASE}/catalog/items/${productId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse<CatalogItem>(res);
}

export async function createCatalogItem(
  payload: CreateCatalogItemRequest,
): Promise<CatalogItem> {
  const res = await fetch(`${API_BASE}/catalog/items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(payload),
  });
  return handleResponse<CatalogItem>(res);
}

export async function updateCatalogItem(
  productId: string,
  patch: UpdateCatalogItemRequest,
): Promise<CatalogItem> {
  const res = await fetch(`${API_BASE}/catalog/items/${productId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(patch),
  });
  return handleResponse<CatalogItem>(res);
}

export async function deleteCatalogItem(productId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/catalog/items/${productId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  if (!res.ok) {
    throw new Error(`Falha ao deletar item (HTTP ${res.status})`);
  }
}

export async function askCatalog(
  query: string,
  categoryId?: string,
  topK?: number,
): Promise<CatalogAskResponse> {
  const res = await fetch(`${API_BASE}/catalog/ask`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ query, categoryId, topK }),
  });
  return handleResponse<CatalogAskResponse>(res);
}

/** Download URL pro template CSV. Sem auth — endpoint público. */
export function getCsvTemplateUrl(): string {
  return `${API_BASE}/catalog/import/template`;
}

export interface CsvImportResult {
  totalRowsParsed: number;
  imported: number;
  items: Array<{ row: number; productId: string; name: string }>;
  parseErrors: Array<{ row: number; reason: string; line?: string }>;
  persistErrors: Array<{ row: number; name: string; reason: string }>;
  cappedAt?: number;
}

export async function importCatalogCsv(csv: string): Promise<CsvImportResult> {
  const res = await fetch(`${API_BASE}/catalog/import/csv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ csv }),
  });
  return handleResponse<CsvImportResult>(res);
}

export interface ScrapeImportPreview {
  preview: CreateCatalogItemRequest & { categoryId: string | null };
  sourceUrl: string;
  latencyMs: number;
}

export interface DeleteAllResult {
  deleted: number;
  pineconeOk: boolean;
  pineconeError?: string;
}

/** Apaga TODOS os items do catálogo da org + hashes + vetores Pinecone. */
export async function deleteAllCatalogItems(): Promise<DeleteAllResult> {
  const res = await fetch(`${API_BASE}/catalog/admin/all`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse<DeleteAllResult>(res);
}

export interface ReindexAllResult {
  processed: number;
  succeeded: number;
  failed: number;
  remaining: number;
}

/** Reindexa todos items existentes (fire-and-forget no backend). */
export async function reindexAllCatalogItems(): Promise<ReindexAllResult> {
  const res = await fetch(`${API_BASE}/catalog/admin/reindex-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
  });
  return handleResponse<ReindexAllResult>(res);
}

export async function scrapeCatalogUrl(
  url: string,
  categoryLabel: string,
  categoryId?: string,
): Promise<ScrapeImportPreview> {
  const res = await fetch(`${API_BASE}/catalog/import/scrape`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ url, categoryLabel, categoryId }),
  });
  return handleResponse<ScrapeImportPreview>(res);
}
